import { useEffect, useState, useCallback } from "react";
import api from "../api/client";

const ESTADOS = ["todos", "pendiente", "confirmado", "preparando", "listo", "entregado", "cancelado"];

const ESTADO_META = {
  pendiente:   { color: "#f59e0b", bg: "#fef3c7", label: "Pendiente" },
  confirmado:  { color: "#3b82f6", bg: "#dbeafe", label: "Confirmado" },
  preparando:  { color: "#f97316", bg: "#ffedd5", label: "Preparando" },
  listo:       { color: "#8b5cf6", bg: "#ede9fe", label: "Listo" },
  entregado:   { color: "#22c55e", bg: "#dcfce7", label: "Entregado" },
  cancelado:   { color: "#ef4444", bg: "#fee2e2", label: "Cancelado" },
};

const FLUJO = {
  pendiente:  ["confirmado", "cancelado"],
  confirmado: ["preparando", "cancelado"],
  preparando: ["listo", "cancelado"],
  listo:      ["entregado", "cancelado"],
  entregado:  [],
  cancelado:  ["pendiente"],
};

function hoy() {
  return new Date().toISOString().split("T")[0];
}

function toBs(usd, tasa) {
  if (!tasa || usd == null) return null;
  return (Number(usd) * tasa).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function EstadoBadge({ estado }) {
  const meta = ESTADO_META[estado] || { color: "#6b7280", bg: "#f3f4f6", label: estado };
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: meta.bg, color: meta.color,
    }}>
      {meta.label}
    </span>
  );
}

function labelDia(fechaStr) {
  const fecha = new Date(fechaStr + "T00:00:00");
  const hoyD = new Date(); hoyD.setHours(0, 0, 0, 0);
  const ayerD = new Date(hoyD); ayerD.setDate(hoyD.getDate() - 1);
  if (fecha.getTime() === hoyD.getTime()) return "Hoy";
  if (fecha.getTime() === ayerD.getTime()) return "Ayer";
  return fecha.toLocaleDateString("es-VE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function agruparPorDia(pedidos) {
  const mapa = {};
  pedidos.forEach((p) => {
    const clave = p.fecha ? p.fecha.substring(0, 10) : "sin-fecha";
    if (!mapa[clave]) mapa[clave] = [];
    mapa[clave].push(p);
  });
  return Object.entries(mapa).sort(([a], [b]) => b.localeCompare(a));
}

/* ─── Modal de detalle ───────────────────────────────────────────────────── */
function PedidoDetalle({ pedido, onClose, onEstadoChange }) {
  const [estado, setEstado] = useState(pedido.estado);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const cambiarEstado = async (nuevoEstado) => {
    setSaving(true); setError("");
    try {
      const { data } = await api.patch(`/pedidos/${pedido.id}/estado`, { estado: nuevoEstado });
      setEstado(data.estado);
      onEstadoChange(data);
    } catch { setError("No se pudo cambiar el estado"); }
    finally { setSaving(false); }
  };

  const tasa = pedido.tasa_bcv && pedido.tasa_bcv > 0 ? pedido.tasa_bcv : null;
  const totalBs = toBs(pedido.total, tasa);
  const fechaStr = pedido.fecha
    ? new Date(pedido.fecha).toLocaleString("es-VE", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

  const accionesSiguientes = FLUJO[estado] || [];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>

        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h3 style={{ fontWeight: 800, fontSize: 17, color: "#0f172a", margin: 0 }}>Pedido #{pedido.id}</h3>
            <p style={{ fontSize: 12, color: "#94a3b8", margin: "3px 0 0" }}>{fechaStr}</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <EstadoBadge estado={estado} />
            <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: "50%", border: "none", background: "#f1f5f9", cursor: "pointer", fontSize: 18, color: "#64748b", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
          </div>
        </div>

        <div style={{ padding: "20px 24px 28px", display: "flex", flexDirection: "column", gap: 20 }}>

          <div style={{ background: "#f8fafc", borderRadius: 14, padding: "14px 16px" }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 10px" }}>Datos del cliente</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ display: "flex", gap: 8, fontSize: 13 }}>
                <span style={{ color: "#64748b", minWidth: 70 }}>Nombre</span>
                <span style={{ fontWeight: 600, color: "#1e293b" }}>{pedido.cliente_nombre}</span>
              </div>
              {pedido.cliente_telefono && (
                <div style={{ display: "flex", gap: 8, fontSize: 13 }}>
                  <span style={{ color: "#64748b", minWidth: 70 }}>Telefono</span>
                  <a href={`tel:${pedido.cliente_telefono}`} style={{ fontWeight: 600, color: "#3b82f6", textDecoration: "none" }}>{pedido.cliente_telefono}</a>
                </div>
              )}
              {pedido.cliente_direccion && (
                <div style={{ display: "flex", gap: 8, fontSize: 13 }}>
                  <span style={{ color: "#64748b", minWidth: 70 }}>Direccion</span>
                  <span style={{ fontWeight: 600, color: "#1e293b" }}>{pedido.cliente_direccion}</span>
                </div>
              )}
              {pedido.notas && (
                <div style={{ display: "flex", gap: 8, fontSize: 13 }}>
                  <span style={{ color: "#64748b", minWidth: 70 }}>Notas</span>
                  <span style={{ color: "#475569", fontStyle: "italic" }}>{pedido.notas}</span>
                </div>
              )}
            </div>
          </div>

          <div>
            <p style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 10px" }}>Productos</p>
            <div style={{ border: "1px solid #f1f5f9", borderRadius: 14, overflow: "hidden" }}>
              {pedido.items?.map((item, i) => {
                const subtotal = Number(item.precio_unitario) * item.cantidad;
                const subtotalBs = toBs(subtotal, tasa);
                return (
                  <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 14px", borderBottom: i < pedido.items.length - 1 ? "1px solid #f8fafc" : "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ width: 24, height: 24, borderRadius: 8, background: "#f1f5f9", color: "#64748b", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{item.cantidad}x</span>
                      <div>
                        <p style={{ fontWeight: 600, fontSize: 13, color: "#1e293b", margin: 0 }}>{item.nombre_producto || `Producto #${item.producto_id}`}</p>
                        <p style={{ fontSize: 11, color: "#94a3b8", margin: 0 }}>${Number(item.precio_unitario).toFixed(2)} c/u</p>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <p style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", margin: 0 }}>${subtotal.toFixed(2)}</p>
                      {subtotalBs && <p style={{ fontSize: 11, color: "#94a3b8", margin: 0 }}>Bs. {subtotalBs}</p>}
                    </div>
                  </div>
                );
              })}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", background: "#f8fafc", borderTop: "2px solid #e2e8f0" }}>
                <span style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>Total</span>
                <div style={{ textAlign: "right" }}>
                  <p style={{ fontWeight: 800, fontSize: 16, color: "#0f172a", margin: 0 }}>${Number(pedido.total).toFixed(2)} {pedido.moneda}</p>
                  {totalBs && <p style={{ fontSize: 12, color: "#64748b", margin: "2px 0 0" }}>Bs. {totalBs}</p>}
                </div>
              </div>
            </div>
            {tasa && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, padding: "6px 10px", background: "#f0fdf4", borderRadius: 8, width: "fit-content" }}>
                <span style={{ fontSize: 11, color: "#16a34a" }}>Tasa BCV al pedido:</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#15803d" }}>Bs. {tasa.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / USD</span>
              </div>
            )}
          </div>

          <div>
            <p style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 12px" }}>Cambiar estado</p>
            {accionesSiguientes.length > 0 && (
              <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                {accionesSiguientes.map((sig) => {
                  const meta = ESTADO_META[sig] || {};
                  return (
                    <button key={sig} onClick={() => cambiarEstado(sig)} disabled={saving}
                      style={{ padding: "9px 18px", borderRadius: 12, border: "none", cursor: saving ? "not-allowed" : "pointer", background: meta.bg, color: meta.color, fontWeight: 700, fontSize: 13, opacity: saving ? 0.6 : 1 }}>
                      {saving ? "..." : `→ ${meta.label}`}
                    </button>
                  );
                })}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <select value={estado} onChange={(e) => setEstado(e.target.value)}
                style={{ flex: 1, border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "9px 12px", fontSize: 13, color: "#1e293b", outline: "none", background: "#fff" }}>
                {ESTADOS.filter((e) => e !== "todos").map((e) => (
                  <option key={e} value={e}>{ESTADO_META[e]?.label || e}</option>
                ))}
              </select>
              <button onClick={() => cambiarEstado(estado)} disabled={saving || estado === pedido.estado}
                style={{ padding: "9px 20px", borderRadius: 10, border: "none", background: "#6366f1", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", opacity: (saving || estado === pedido.estado) ? 0.5 : 1 }}>
                {saving ? "..." : "Guardar"}
              </button>
            </div>
            {error && <p style={{ fontSize: 12, color: "#ef4444", marginTop: 8 }}>{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Fila de pedido ─────────────────────────────────────────────────────── */
function FilaPedido({ p, onClick }) {
  const tasa = p.tasa_bcv && p.tasa_bcv > 0 ? p.tasa_bcv : null;
  const bs = tasa ? (Number(p.total) * tasa).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : null;
  const hora = p.fecha
    ? new Date(p.fecha).toLocaleTimeString("es-VE", { hour: "2-digit", minute: "2-digit" })
    : "—";
  return (
    <tr className="hover:bg-indigo-50/30 cursor-pointer transition-colors" onClick={onClick}>
      <td className="px-5 py-3 font-mono text-gray-400 text-xs">#{p.id}</td>
      <td className="px-5 py-3">
        <p className="font-semibold text-gray-800 text-sm">{p.cliente_nombre}</p>
        {p.cliente_telefono && <p className="text-xs text-gray-400">{p.cliente_telefono}</p>}
      </td>
      <td className="px-5 py-3 text-right">
        <span className="font-semibold text-gray-800">${Number(p.total).toFixed(2)}</span>
      </td>
      <td className="px-5 py-3 text-right hidden lg:table-cell">
        {bs ? <span className="text-green-700 font-medium text-sm">Bs. {bs}</span> : <span className="text-gray-300 text-xs">—</span>}
      </td>
      <td className="px-5 py-3 text-center"><EstadoBadge estado={p.estado} /></td>
      <td className="px-5 py-3 text-gray-400 text-xs text-center">{hora}</td>
      <td className="px-5 py-3 text-center">
        <span className="text-indigo-600 hover:text-indigo-800 text-xs font-medium">Detalles</span>
      </td>
    </tr>
  );
}

/* ─── Página principal ───────────────────────────────────────────────────── */
export default function Pedidos() {
  const [pedidos, setPedidos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const PAGE_SIZE = 50;

  // Filtros
  const [estado, setEstado] = useState("todos");
  const [fechaDesde, setFechaDesde] = useState(hoy());
  const [fechaHasta, setFechaHasta] = useState(hoy());
  const [filtrosActivos, setFiltrosActivos] = useState({ estado: "todos", fechaDesde: hoy(), fechaHasta: hoy() });

  const cargar = useCallback((f = filtrosActivos, p = page) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, page_size: PAGE_SIZE });
    if (f.estado !== "todos") params.append("estado", f.estado);
    if (f.fechaDesde) params.append("fecha_desde", f.fechaDesde);
    if (f.fechaHasta) params.append("fecha_hasta", f.fechaHasta);
    api.get(`/pedidos/?${params.toString()}`)
      .then((r) => { setPedidos(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [filtrosActivos, page]);

  useEffect(() => { cargar(filtrosActivos, page); }, [page, filtrosActivos]);

  const aplicarFiltros = () => {
    const f = { estado, fechaDesde, fechaHasta };
    setFiltrosActivos(f);
    setPage(1);
  };

  const limpiarFiltros = () => {
    const f = { estado: "todos", fechaDesde: hoy(), fechaHasta: hoy() };
    setEstado("todos");
    setFechaDesde(hoy());
    setFechaHasta(hoy());
    setFiltrosActivos(f);
    setPage(1);
  };

  const handleEstadoChange = (updated) => {
    setPedidos((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    setDetalle(updated);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const grupos = agruparPorDia(pedidos);

  const totalUSD = pedidos.reduce((s, p) => s + Number(p.total), 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Pedidos</h2>
          <p className="text-gray-500 text-sm mt-1">
            {total} pedido{total !== 1 ? "s" : ""}
            {total > 0 && ` · $${totalUSD.toFixed(2)} en pantalla`}
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
        {/* Fecha */}
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
            <input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
            <input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex gap-2">
            {/* Accesos rápidos */}
            {[
              { label: "Hoy", fn: () => { const h = hoy(); setFechaDesde(h); setFechaHasta(h); } },
              { label: "Ayer", fn: () => { const a = new Date(); a.setDate(a.getDate() - 1); const s = a.toISOString().split("T")[0]; setFechaDesde(s); setFechaHasta(s); } },
              { label: "7 dias", fn: () => { const a = new Date(); a.setDate(a.getDate() - 6); setFechaDesde(a.toISOString().split("T")[0]); setFechaHasta(hoy()); } },
              { label: "Este mes", fn: () => { const h = new Date(); const ini = new Date(h.getFullYear(), h.getMonth(), 1).toISOString().split("T")[0]; setFechaDesde(ini); setFechaHasta(hoy()); } },
            ].map(({ label, fn }) => (
              <button key={label} onClick={fn}
                className="text-xs font-medium px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700 transition-colors">
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Estado + botones */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500">Estado:</span>
          {ESTADOS.map((e) => {
            const meta = ESTADO_META[e];
            const active = estado === e;
            return (
              <button key={e} onClick={() => setEstado(e)}
                style={active && meta ? { background: meta.color, color: "#fff", border: "none" } : {}}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${active && !meta ? "bg-gray-800 text-white border-none" : !active ? "bg-white border border-gray-200 text-gray-600 hover:border-indigo-400" : ""}`}>
                {e === "todos" ? "Todos" : (meta?.label || e)}
              </button>
            );
          })}
          <div className="ml-auto flex gap-2">
            <button onClick={aplicarFiltros}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
              Aplicar
            </button>
            <button onClick={limpiarFiltros}
              className="border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors">
              Hoy
            </button>
          </div>
        </div>
      </div>

      {/* Contenido */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : pedidos.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm text-center py-16 text-gray-400">
          <div className="text-5xl mb-3">📦</div>
          <p className="font-medium">No hay pedidos para este periodo</p>
          <p className="text-sm mt-1">Prueba cambiando el rango de fechas</p>
        </div>
      ) : (
        <div className="space-y-5">
          {grupos.map(([diaKey, items]) => {
            const totalDia = items.reduce((s, p) => s + Number(p.total), 0);
            const pendientesDia = items.filter((p) => !["entregado", "cancelado"].includes(p.estado)).length;
            return (
              <div key={diaKey} className="bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Cabecera del día */}
                <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-50 to-white border-b border-indigo-100">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-indigo-500" />
                    <span className="font-bold text-gray-800 capitalize text-sm">
                      {labelDia(diaKey)}
                    </span>
                    <span className="text-xs text-gray-400">
                      {items.length} pedido{items.length !== 1 ? "s" : ""}
                    </span>
                    {pendientesDia > 0 && (
                      <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                        {pendientesDia} en proceso
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-gray-900 text-sm">${totalDia.toFixed(2)}</span>
                    <span className="text-xs text-gray-400 ml-1">total del dia</span>
                  </div>
                </div>

                {/* Tabla del día */}
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-400 uppercase text-xs">
                    <tr>
                      <th className="px-5 py-2 text-left">#</th>
                      <th className="px-5 py-2 text-left">Cliente</th>
                      <th className="px-5 py-2 text-right">USD</th>
                      <th className="px-5 py-2 text-right hidden lg:table-cell">Bs.</th>
                      <th className="px-5 py-2 text-center">Estado</th>
                      <th className="px-5 py-2 text-center">Hora</th>
                      <th className="px-5 py-2 text-center">Ver</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {items.map((p) => (
                      <FilaPedido key={p.id} p={p} onClick={() => setDetalle(p)} />
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 bg-white rounded-xl shadow-sm text-sm text-gray-500">
              <span>Pagina {page} de {totalPages} — {total} pedidos</span>
              <div className="flex gap-2">
                <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40">Anterior</button>
                <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40">Siguiente</button>
              </div>
            </div>
          )}
        </div>
      )}

      {detalle && (
        <PedidoDetalle
          pedido={detalle}
          onClose={() => setDetalle(null)}
          onEstadoChange={handleEstadoChange}
        />
      )}
    </div>
  );
}
